
import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { labelTemplates, customFields } from '../../utils/api';
import { toast } from 'react-toastify';

const CreateTemplateForm = ({ open, onClose, onSuccess, template }) => {
  const FILTER_FIELDS = [
    { id: 'assayer', label: 'Assayer', type: 'text' },
    { id: 'cac_sticker', label: 'CAC Sticker', type: 'select', options: ['green', 'gold', 'none'] },
    { id: 'certification_number', label: 'Certification Number', type: 'text' },
    { id: 'certification_service_id', label: 'Certification Service', type: 'text' },
    { id: 'coin_details', label: 'Coin Details', type: 'text' },
    { id: 'composition', label: 'Composition', type: 'text' },
    { id: 'country', label: 'Country', type: 'text' },
    { id: 'current_pop_higher', label: 'Current Pop Higher', type: 'number' },
    { id: 'current_population', label: 'Current Population', type: 'number' },
    { id: 'denomination', label: 'Denomination', type: 'text' },
    { id: 'diameter', label: 'Diameter', type: 'number' },
    { id: 'die_variety', label: 'Die Variety', type: 'text' },
    { id: 'edge', label: 'Edge', type: 'text' },
    { id: 'grade', label: 'Grade', type: 'text' },
    { id: 'grading_cost', label: 'Grading Cost', type: 'number' },
    { id: 'list_price', label: 'List Price', type: 'number' },
    { id: 'metal', label: 'Metal', type: 'text' },
    { id: 'minor_variety', label: 'Minor Variety', type: 'text' },
    { id: 'mint_mark', label: 'Mint Mark', type: 'text' },
    { id: 'mintage', label: 'Mintage', type: 'text' },
    { id: 'notes', label: 'Notes', type: 'text' },
    { id: 'pcgs_number', label: 'PCGS Number', type: 'text' },
    { id: 'pop_higher_when_bought', label: 'Pop Higher When Bought', type: 'number' },
    { id: 'population_when_bought', label: 'Population When Bought', type: 'number' },
    { id: 'price_guide_value', label: 'Price Guide Value', type: 'number' },
    { id: 'provenance', label: 'Provenance', type: 'text' },
    { id: 'purchase_date', label: 'Purchase Date', type: 'date' },
    { id: 'purchase_price', label: 'Purchase Price', type: 'number' },
    { id: 'reference_number', label: 'Reference Number', type: 'text' },
    { id: 'shipping_cost', label: 'Shipping Cost', type: 'number' },
    { id: 'status', label: 'Status', type: 'text' },
    { id: 'storage_location', label: 'Storage Location', type: 'text' },
    { id: 'total_cost', label: 'Total Cost', type: 'number' },
    { id: 'variety', label: 'Variety', type: 'text' },
    { id: 'weight', label: 'Weight', type: 'number' },
    { id: 'year', label: 'Year', type: 'text' }
  ];

  const initializeFormData = (templateData) => {
    if (templateData) {
      // Convert any simple field IDs to styled field objects
      const styledFields = (templateData.coin_fields || []).map(field => {
        if (typeof field === 'string' || !field.id) {
          return {
            id: field.id || field,
            text_align: 'left',
            font_size_pt: 12,
            line_height_em: 1.5
          };
        }
        return field;
      });

      return {
        name: templateData.name,
        type: templateData.type,
        description: templateData.description || '',
        width_mm: templateData.width_mm.toString(),
        height_mm: templateData.height_mm.toString(),
        margin_top_mm: (templateData.margin_top_mm || 0).toString(),
        margin_right_mm: (templateData.margin_right_mm || 0).toString(),
        margin_bottom_mm: (templateData.margin_bottom_mm || 0).toString(),
        margin_left_mm: (templateData.margin_left_mm || 0).toString(),
        fields: templateData.fields || [],
        coin_fields: styledFields
      };
    }

    return {
      name: '',
      type: '',
      description: '',
      width_mm: '',
      height_mm: '',
      margin_top_mm: '0',
      margin_right_mm: '0',
      margin_bottom_mm: '0',
      margin_left_mm: '0',
      fields: [],
      coin_fields: []
    };
  };

  const [formData, setFormData] = useState(initializeFormData(template));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableCustomFields, setAvailableCustomFields] = useState([]);

  useEffect(() => {
    setFormData(initializeFormData(template));
  }, [template]);

  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const fields = await customFields.getAll();
        setAvailableCustomFields(fields);
      } catch (err) {
        console.error('Failed to load custom fields:', err);
        toast.error('Failed to load custom fields');
      }
    };

    loadCustomFields();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = {
        ...formData,
        width_mm: parseFloat(formData.width_mm),
        height_mm: parseFloat(formData.height_mm),
        margin_top_mm: parseFloat(formData.margin_top_mm),
        margin_right_mm: parseFloat(formData.margin_right_mm),
        margin_bottom_mm: parseFloat(formData.margin_bottom_mm),
        margin_left_mm: parseFloat(formData.margin_left_mm),
        coin_fields: formData.coin_fields || []
      };

      const result = template 
        ? await labelTemplates.update(template.id, data)
        : await labelTemplates.create(data);
      onSuccess?.(result);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isFieldSelected = (fieldId) => {
    return formData.coin_fields?.some(f => f.id === fieldId);
  };

  const toggleField = (fieldId) => {
    const newFields = isFieldSelected(fieldId)
      ? formData.coin_fields.filter(f => f.id !== fieldId)
      : [...(formData.coin_fields || []), {
          id: fieldId,
          text_align: 'left',
          font_size_pt: 12,
          line_height_em: 1.5
        }];

    setFormData(prev => ({
      ...prev,
      coin_fields: newFields
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
        <Dialog.Panel className="mx-auto max-w-3xl w-full my-8 max-h-[90vh] bg-white rounded-lg shadow-xl overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm">
            <Dialog.Title className="text-lg font-medium">
              {template ? 'Edit Label Template' : 'Create Label Template'}
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 p-4 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Template Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Template Type
                </label>
                <select
                  id="type"
                  name="type"
                  required
                  value={formData.type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">Select a type</option>
                  <option value="qr_code">QR Code</option>
                  <option value="barcode">Barcode</option>
                  <option value="text_label">Text Label</option>
                </select>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="width_mm" className="block text-sm font-medium text-gray-700">
                    Width (mm)
                  </label>
                  <input
                    type="number"
                    id="width_mm"
                    name="width_mm"
                    required
                    min="0"
                    step="0.1"
                    value={formData.width_mm}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="height_mm" className="block text-sm font-medium text-gray-700">
                    Height (mm)
                  </label>
                  <input
                    type="number"
                    id="height_mm"
                    name="height_mm"
                    required
                    min="0"
                    step="0.1"
                    value={formData.height_mm}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="margin_top_mm" className="block text-sm font-medium text-gray-700">
                    Top Margin (mm)
                  </label>
                  <input
                    type="number"
                    id="margin_top_mm"
                    name="margin_top_mm"
                    min="0"
                    step="0.1"
                    value={formData.margin_top_mm}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="margin_right_mm" className="block text-sm font-medium text-gray-700">
                    Right Margin (mm)
                  </label>
                  <input
                    type="number"
                    id="margin_right_mm"
                    name="margin_right_mm"
                    min="0"
                    step="0.1"
                    value={formData.margin_right_mm}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="margin_bottom_mm" className="block text-sm font-medium text-gray-700">
                    Bottom Margin (mm)
                  </label>
                  <input
                    type="number"
                    id="margin_bottom_mm"
                    name="margin_bottom_mm"
                    min="0"
                    step="0.1"
                    value={formData.margin_bottom_mm}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="margin_left_mm" className="block text-sm font-medium text-gray-700">
                    Left Margin (mm)
                  </label>
                  <input
                    type="number"
                    id="margin_left_mm"
                    name="margin_left_mm"
                    min="0"
                    step="0.1"
                    value={formData.margin_left_mm}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Coin Fields to Include
              </label>
              <div className="mt-2 grid grid-cols-2 gap-4 pb-4 border-b">
                <div className="col-span-2 font-medium text-gray-700 text-sm mb-2">Standard Fields</div>
                {FILTER_FIELDS.map(field => (
                  <div key={field.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`coin-field-${field.id}`}
                      checked={isFieldSelected(field.id)}
                      onChange={() => toggleField(field.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`coin-field-${field.id}`}
                      className="ml-2 block text-sm text-gray-900"
                    >
                      {field.label}
                    </label>
                  </div>
                ))}
              </div>

              {availableCustomFields.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="col-span-2 font-medium text-gray-700 text-sm mb-2">Custom Fields</div>
                  {availableCustomFields.map(field => {
                    const fieldId = `custom_field_${field.id}`;
                    return (
                      <div key={fieldId} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`coin-field-custom-${field.id}`}
                          checked={isFieldSelected(fieldId)}
                          onChange={() => toggleField(fieldId)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`coin-field-custom-${field.id}`}
                          className="ml-2 block text-sm text-gray-900"
                        >
                          {field.name}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (template ? 'Saving...' : 'Creating...') : (template ? 'Save Changes' : 'Create Template')}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default CreateTemplateForm;
